import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  sentryApi,
  type SentryAlert,
  type SentryAlertStatus,
  type SentryScore,
} from '../services/api';
import { useToast } from '../context/ToastContext';
import { Spinner } from '../components/shared/Spinner';

/**
 * Module « Tickets dev » — suivi des alertes d'erreurs Sentry poussées par
 * BizCoach (webhook /hooks/sentry). Distinct du module factures.
 *
 * Cycle de suivi : a_traiter → pris_en_charge → realise → deploye.
 * Au passage « déployé », on saisit la date de déploiement + le n° de commit.
 */

const STATUS_LABEL: Record<SentryAlertStatus, string> = {
  a_traiter: 'À traiter',
  pris_en_charge: 'Pris en charge',
  realise: 'Réalisé',
  deploye: 'Déployé',
};
const STATUS_BADGE: Record<SentryAlertStatus, string> = {
  a_traiter: 'bg-slate-100 text-slate-700 ring-slate-200',
  pris_en_charge: 'bg-blue-100 text-blue-800 ring-blue-200',
  realise: 'bg-amber-100 text-amber-800 ring-amber-200',
  deploye: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
};

// Transition « suivante » proposée en bouton principal.
const NEXT_STATUS: Partial<Record<SentryAlertStatus, SentryAlertStatus>> = {
  a_traiter: 'pris_en_charge',
  pris_en_charge: 'realise',
  realise: 'deploye',
};

const SCORE_LABEL: Record<NonNullable<SentryScore>, string> = {
  high: 'Critique',
  medium: 'Moyen',
  low: 'Faible',
};
const SCORE_BADGE: Record<NonNullable<SentryScore>, string> = {
  high: 'bg-red-100 text-red-800 ring-red-200',
  medium: 'bg-orange-100 text-orange-800 ring-orange-200',
  low: 'bg-slate-100 text-slate-500 ring-slate-200',
};

const FILTERS: Array<{ value: '' | SentryAlertStatus; label: string }> = [
  { value: '', label: 'Toutes' },
  { value: 'a_traiter', label: 'À traiter' },
  { value: 'pris_en_charge', label: 'Pris en charge' },
  { value: 'realise', label: 'Réalisé' },
  { value: 'deploye', label: 'Déployé' },
];

function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function SentryTicketsPage() {
  // Défaut « À traiter » : c'est la file prioritaire qu'on veut voir d'abord.
  const [filter, setFilter] = useState<'' | SentryAlertStatus>('a_traiter');
  const toast = useToast();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['sentry', 'alerts', filter],
    queryFn: () => sentryApi.list(filter || undefined),
    staleTime: 30_000,
  });

  const mutation = useMutation({
    mutationFn: ({
      id,
      status,
      date_deploiement,
      num_commit,
    }: {
      id: string;
      status: SentryAlertStatus;
      date_deploiement?: string;
      num_commit?: string;
    }) => sentryApi.updateStatus(id, { status, date_deploiement, num_commit }),
    onSuccess: (_resp, vars) => {
      qc.invalidateQueries({ queryKey: ['sentry', 'alerts'] });
      toast.success(`Statut → ${STATUS_LABEL[vars.status]}`);
    },
    onError: (err) => toast.error(`Mise à jour échouée : ${String(err)}`),
  });

  const alerts = q.data?.alerts ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Tickets dev</h1>
        <p className="text-sm text-slate-500 mt-1">
          Alertes d'erreurs Sentry remontées par BizCoach, déjà vulgarisées et
          accompagnées d'un prompt de fix prêt à coller dans Claude Code.
        </p>
      </div>

      {/* Filtres par statut */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {FILTERS.map((f) => (
          <button
            key={f.value || 'all'}
            type="button"
            onClick={() => setFilter(f.value)}
            className={`px-3 py-1.5 rounded-md transition-colors ${
              filter === f.value
                ? 'bg-slate-900 text-white'
                : 'text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}
          >
            {f.label}
          </button>
        ))}
        {q.isFetching && <Spinner />}
      </div>

      {q.isLoading ? (
        <p className="text-slate-500">Chargement…</p>
      ) : q.isError ? (
        <p className="text-red-600">Erreur : {String(q.error)}</p>
      ) : alerts.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-500">
          Aucune alerte pour ce filtre.
        </div>
      ) : (
        <div className="space-y-4">
          {alerts.map((a) => (
            <AlertCard
              key={a.id}
              alert={a}
              busy={mutation.isPending && mutation.variables?.id === a.id}
              onAdvance={(status, extra) =>
                mutation.mutate({ id: a.id, status, ...extra })
              }
              onCopyPrompt={async () => {
                const prompt = a.payload?.fixPrompt;
                if (!prompt) {
                  toast.error('Aucun prompt de fix dans cette alerte.');
                  return;
                }
                try {
                  await navigator.clipboard.writeText(prompt);
                  toast.success('Prompt copié dans le presse-papier.');
                } catch {
                  toast.error('Copie impossible (presse-papier indisponible).');
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AlertCard({
  alert,
  busy,
  onAdvance,
  onCopyPrompt,
}: {
  alert: SentryAlert;
  busy: boolean;
  onAdvance: (
    status: SentryAlertStatus,
    extra?: { date_deploiement?: string; num_commit?: string },
  ) => void;
  onCopyPrompt: () => void;
}) {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [deployDate, setDeployDate] = useState('');
  const [deployCommit, setDeployCommit] = useState('');

  const next = NEXT_STATUS[alert.status];
  const signals = alert.payload?.signals;

  function confirmDeploy() {
    if (!deployDate || !deployCommit.trim()) return;
    // Le backend attend une date ISO ; <input type="date"> fournit YYYY-MM-DD,
    // qu'on transforme en ISO (minuit UTC suffit pour une date de déploiement).
    const iso = new Date(`${deployDate}T00:00:00Z`).toISOString();
    onAdvance('deploye', { date_deploiement: iso, num_commit: deployCommit.trim() });
    setDeploying(false);
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-3">
      {/* En-tête : score, issue, statut */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {alert.score && (
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${SCORE_BADGE[alert.score]}`}
            >
              {SCORE_LABEL[alert.score]}
            </span>
          )}
          {alert.permalink ? (
            <a
              href={alert.permalink}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-sm font-semibold text-blue-700 hover:underline"
            >
              {alert.short_id}
            </a>
          ) : (
            <span className="font-mono text-sm font-semibold">{alert.short_id}</span>
          )}
          {alert.level && (
            <span className="text-xs text-slate-400 uppercase">{alert.level}</span>
          )}
          {alert.first_party === false && (
            <span
              className="text-xs text-slate-400 italic"
              title="Erreur hors de notre code (réseau / navigateur) : souvent non corrigeable en code"
            >
              hors-code
            </span>
          )}
        </div>
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${STATUS_BADGE[alert.status]}`}
        >
          {STATUS_LABEL[alert.status]}
        </span>
      </div>

      {/* Titre technique */}
      {alert.title && (
        <p className="font-mono text-xs text-slate-700 break-words">{alert.title}</p>
      )}

      {/* Vulgarisation */}
      {alert.vulgarization && (
        <p className="text-sm text-slate-800 leading-relaxed">{alert.vulgarization}</p>
      )}

      {/* Signaux + dates */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
        {signals?.count != null && <span>{signals.count} occurrence(s)</span>}
        {signals?.userCount != null && <span>{signals.userCount} utilisateur(s)</span>}
        {signals?.environment && <span>env : {signals.environment}</span>}
        {signals?.handled === false && <span className="text-red-500">non rattrapé</span>}
        <span>Reçue le {formatDateTime(alert.received_at)}</span>
      </div>

      {/* Infos de déploiement (si déployé) */}
      {alert.status === 'deploye' && (alert.num_commit || alert.date_deploiement) && (
        <div className="text-xs text-emerald-700 bg-emerald-50 rounded-md px-3 py-2">
          Déployé le {formatDateTime(alert.date_deploiement)} ·{' '}
          <span className="font-mono">{alert.num_commit ?? '-'}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        {alert.payload?.fixPrompt && (
          <button
            type="button"
            onClick={onCopyPrompt}
            className="px-3 py-1.5 rounded-md text-sm bg-slate-900 text-white hover:bg-slate-700"
          >
            Copier le prompt
          </button>
        )}
        {alert.payload?.fixPrompt && (
          <button
            type="button"
            onClick={() => setShowPrompt((v) => !v)}
            className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100 border border-slate-200"
          >
            {showPrompt ? 'Masquer le prompt' : 'Voir le prompt'}
          </button>
        )}

        <div className="ml-auto flex items-center gap-2">
          {/* Bouton transition suivante */}
          {next && next !== 'deploye' && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onAdvance(next)}
              className="px-3 py-1.5 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50"
            >
              → {STATUS_LABEL[next]}
            </button>
          )}
          {next === 'deploye' && !deploying && (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setDeploying(true);
                setDeployDate('');
                setDeployCommit('');
              }}
              className="px-3 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
            >
              → Déployé…
            </button>
          )}
        </div>
      </div>

      {/* Formulaire de déploiement */}
      {deploying && (
        <div className="border-t border-slate-200 pt-3 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-slate-600">
            Date de déploiement
            <input
              type="date"
              value={deployDate}
              onChange={(e) => setDeployDate(e.target.value)}
              className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-600">
            N° de commit
            <input
              type="text"
              value={deployCommit}
              onChange={(e) => setDeployCommit(e.target.value)}
              placeholder="ex. a1b2c3d"
              className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </label>
          <button
            type="button"
            disabled={busy || !deployDate || !deployCommit.trim()}
            onClick={confirmDeploy}
            className="px-3 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
          >
            Confirmer le déploiement
          </button>
          <button
            type="button"
            onClick={() => setDeploying(false)}
            className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100"
          >
            Annuler
          </button>
        </div>
      )}

      {/* Prompt de fix déplié */}
      {showPrompt && alert.payload?.fixPrompt && (
        <pre className="border-t border-slate-200 pt-3 text-xs text-slate-700 whitespace-pre-wrap break-words font-mono bg-slate-50 rounded-md p-3 max-h-96 overflow-auto">
          {alert.payload.fixPrompt}
        </pre>
      )}
    </div>
  );
}
